import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Loader2, CheckCircle2 } from "lucide-react";
import AuthShell from "../components/AuthShell";
import PasswordInput from "../components/PasswordInput";
import { api } from "../utils/api";

export default function ResetPasswordNew() {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || "";
  const resetToken = location.state?.resetToken || "";
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!email || !resetToken) {
      navigate("/forgot-password", { replace: true });
    }
  }, [email, resetToken, navigate]);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (form.password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    if (form.password !== form.confirm) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      await api.post("/auth/reset-password", {
        email,
        resetToken,
        password: form.password,
      });
      setDone(true);
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  if (!email || !resetToken) return null;

  return (
    <AuthShell
      title={done ? "Password reset" : "Set new password"}
      subtitle={done ? "" : "Choose a strong password for your account."}
    >
      {done ? (
        <div className="space-y-4">
          <div className="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 p-4">
            <CheckCircle2 className="h-5 w-5 flex-none text-green-600 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-green-800">Password updated successfully</p>
              <p className="mt-1 text-sm text-green-700">
                You can now sign in with your new password.
              </p>
            </div>
          </div>
          <Link to="/signin" className="btn-primary w-full inline-flex items-center justify-center">
            Sign in
          </Link>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <div>
            <label htmlFor="password" className="label-base">New password</label>
            <PasswordInput
              id="password"
              autoComplete="new-password"
              value={form.password}
              onChange={update("password")}
              placeholder="At least 8 characters"
            />
          </div>
          <div>
            <label htmlFor="confirm" className="label-base">Confirm password</label>
            <PasswordInput
              id="confirm"
              autoComplete="new-password"
              value={form.confirm}
              onChange={update("confirm")}
              placeholder="Re-enter password"
            />
          </div>
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}
          <button type="submit" className="btn-primary w-full" disabled={loading}>
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            Reset password
          </button>
        </form>
      )}
    </AuthShell>
  );
}
